<template>
	<article class="cmp cmp-product-thumb" :data-testing="item.variation.id" ref="categoryItem">
		<div :class="paddingClasses" :style="paddingInlineStyles">
            <add-to-basket
				v-if="enableAtc"
				data-testing="item-add-to-basket"
				:variation-id="item.variation.id"
				:is-salable="!!item.filter && item.filter.isSalable"
				:has-children="!!item.filter && item.filter.hasActiveChildren"
				:interval-quantity="item.variation.intervalOrderQuantity || 1"
				:minimum-quantity="item.variation.minimumOrderQuantity"
				:maximum-quantity="!!item.variation.maximumOrderQuantity && item.variation.maximumOrderQuantity > 0 ? item.variation.maximumOrderQuantity : null"
				:order-properties="item.properties.filter(function(prop) { return prop.property.isOderProperty })"
				:has-order-properties="item.hasOrderProperties"
				:has-required-order-property="item.hasRequiredOrderProperty"
				:use-large-scale="true"
				:show-quantity="false"
				:item-url="item | itemURL(urlWithVariationId)"
				:has-price="item | hasItemDefaultPrice"
				:item-type="item.item.itemType">
            </add-to-basket>

			<div class="thumb-image">
				<div class="prop-1-1">
					<slot name="item-image">
						<lazy-hydrate when-idle>
							<category-image-carousel
								:image-urls-data="item.images | itemImages(currentUrlAccessor)| itemImagesUrlOverwrite(overwriteUrl, imageOptions)"
								:alt-text="item | itemName"
								:title-text="item | itemName"
								:item-url="item | itemURL(urlWithVariationId)"
								:enable-carousel="$ceres.config.item.enableImageCarousel"
								:disable-carousel-on-mobile="disableCarouselOnMobile"
								:disable-lazy-load="disableLazyLoad"
								v-show="!isSibling"
								ref="categoryImageCarousel"
							>
							</category-image-carousel>
						</lazy-hydrate>
						<a v-if="isSibling" :href="getSiblingUrl(selectedSibling.url)">
							<img
								:src="convertFormat(siblingImageURL)"
								ref="imageSibling"
								@mouseover="showSecondImage"
								@mouseleave="hideSecondImage"
							/>
						</a>
					</slot>
				</div>
			</div>

			<template v-if="categoryItemSiblingsEnabled">
				<div class="siblings-container">
					<client-only>
						<lazy-hydrate when-idle>
							<category-item-siblings
								:item-id="item.variation.itemId"
								:variation-id="item.variation.id"
								:variation-link="item | itemURL(urlWithVariationId)"
								@changeVariation="changeVariation($event)"
							/>
						</lazy-hydrate>
					</client-only>
				</div>
			</template>

			<!-- ITEM DETAILS -->
			<slot name="item-details">
				<div class="thumb-content">
					<div class="availability-wrapper">
						<div
							v-if="item.variation.availability"
							:class="'availability availability_' + item.variation.availability.id"
						>
							<div
								v-if="
									item.variation.availability.id == 1 &&
									$translate('Eros::Template.availabilityAlternative') !== ''
								"
							>
								{{ $translate("Eros::Template.availabilityAlternative") }}
							</div>
							<div v-else>
								<div v-if="item.variation.availability.names[0]">
									{{ item.variation.availability.names[0].name }}
								</div>
								<div v-else>{{ item.variation.availability.names.name }}</div>
							</div>
						</div>
						<slot name="ratings"></slot>
					</div>
					<div class="thumb-meta mt-2">
						<a
							:href="item | itemURL(urlWithVariationId)"
							class="thumb-title small"
							:class="{ 'stretched-link': $ceres.config.global.shippingCostsCategoryId == 0 }"
							:aria-label="item | itemName"
						>
							{{ item | itemName
							}}<!--
                    --><span v-for="attribute in item.groupedAttributes">{{
								"Ceres::Template.itemGroupedAttribute" | translate(attribute)
							}}</span>
						</a>
						<slot name="before-prices"></slot>

						<div class="prices">
							<div class="price">
								<template v-if="item.item.itemType === 'set'">
									{{ $translate("Ceres::Template.itemSetPrice", { price: itemSetPrice }) }}
								</template>
								<template
									v-else-if="
										!!item.item && item.item.salableVariationCount > 1 && $ceres.isCheapestSorting
									"
								>
									{{ $translate("Ceres::Template.itemFromPrice", { price: itemPrice }) }}
								</template>
								<template v-else>
									{{
										item.prices.default.unitPrice.formatted
											| specialOffer(item.prices, "unitPrice", "formatted")
									}}
								</template>
							</div>

							<div
								v-if="
									item.prices.rrp &&
									item.prices.rrp.price.value > 0 &&
									item.prices.rrp.price.value > item.prices.default.price.value
								"
								class="price-view-port"
							>
								<del class="crossprice" v-if="item.prices.specialOffer">
									{{ item.prices.default.unitPrice.formatted | itemCrossPrice(true) }}
								</del>
								<del class="crossprice" v-else>
									{{ item.prices.rrp.unitPrice.formatted | itemCrossPrice }}
								</del>
							</div>
						</div>
					</div>

					<add-to-basket
						v-if="enableAtc"
						data-testing="item-add-to-basket"
						:variation-id="item.variation.id"
						:is-salable="!!item.filter && item.filter.isSalable"
						:has-children="!!item.filter && item.filter.hasActiveChildren"
						:interval-quantity="item.variation.intervalOrderQuantity || 1"
						:minimum-quantity="item.variation.minimumOrderQuantity"
						:maximum-quantity="!!item.variation.maximumOrderQuantity && item.variation.maximumOrderQuantity > 0 ? item.variation.maximumOrderQuantity : null"
						:order-properties="item.properties.filter(function(prop) { return prop.property.isOderProperty })"
						:has-order-properties="item.hasOrderProperties"
						:has-required-order-property="item.hasRequiredOrderProperty"
						:use-large-scale="false"
						:show-quantity="false"
						:item-url="item | itemURL(urlWithVariationId)"
						:has-price="item | hasItemDefaultPrice"
						:item-type="item.item.itemType">
					</add-to-basket>

					<slot name="after-prices"></slot>

					<client-only>
						<category-item-sibling-usp
							:item-id="item.variation.itemId"
							:variation-id="item.variation.id"
							:variation-link="item | itemURL(urlWithVariationId)"
						/>
					</client-only>
				</div>
			</slot>
			<!-- ./ITEM DETAILS  -->
		</div>
	</article>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { isNullOrUndefined } from "../../../ceres/helper/utils";
import CategoryImageCarousel from "./CategoryImageCarousel.vue";
import CategoryItemSiblingUsp from "./CategoryItemSiblingUSP.vue";
import CategoryItemSiblings from "./CategoryItemSiblings.vue";
import ItemStoreSpecial from "../../../ceres/components/itemList/ItemStoreSpecial.vue";
import { ImageConverterMixin } from "../../mixins/imageConverter";

export default {
	mixins: [ImageConverterMixin],

	components: {
		CategoryImageCarousel,
		ItemStoreSpecial,
		CategoryItemSiblingUsp,
		CategoryItemSiblings
	},

	props: {
		decimalCount: {
			type: Number,
			default: 0
		},
		imageUrlAccessor: {
			type: String,
			default: "urlMiddle"
		},
		itemData: {
			type: Object
		},
		disableCarouselOnMobile: {
			type: Boolean
		},
		paddingClasses: {
			type: String,
			default: null
		},
		paddingInlineStyles: {
			type: String,
			default: null
		},
        forceUrlWithVariationId:
        {
            type: Boolean,
            default: false
        },
		siblingItemList: {
			type: Object,
			default() {
				return {};
			}
		},
		enableSiblingsCall: {
			type: Boolean,
			default: true
		},
		disableLazyLoad: {
			type: Boolean,
			default: false
		},
		overwriteUrl: {
			type: String,
			default: ""
		},
		imageOptions: {
			type: Object,
			default() {
				return {};
			}
		},
		initiator: {
			type: String,
			default: "itemGrid"
		},
		isFirst: {
			type: Boolean,
			default: false
		},
		enableAtc: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			selectedSibling: {
				imageURL: {
					urlMiddle: ""
				},
				images: {
					first: {
						urlMiddle: "",
						urlPreview: ""
					},
					second: {
						urlMiddle: "",
						urlPreview: ""
					}
				},
				url: "",
				price: 0
			},
			secondSiblingSelected: false,
			categoryItemSiblingsEnabled: App.Eros.Config.itemlists.enableCategoryItemSiblings || false,
			siblingItemListKeys: [],
			siblingsCalled: false
		};
	},

	beforeMount() {
		if (App.Eros.Config.general.benchmarkJS) console.time("App.eros.components.itemList.CategoryItem: mount_" + this.item.variation.id); // eslint-disable-line no-console
		this.categoryItemSiblingsEnabled = this.enableSiblingsCall;
	},

	mounted() {
		if (App.Eros.Config.general.benchmarkJS) console.timeEnd("App.eros.components.itemList.CategoryItem: mount_" + this.item.variation.id); // eslint-disable-line no-console
		if (App.Eros.Config.general.benchmarkJS) console.time("App.eros.components.itemList.CategoryItem: mounted_" + this.item.variation.id); // eslint-disable-line no-console
		const siblingItemList = !isNullOrUndefined(this.siblingItemList) ? this.siblingItemList : {};
		
		this.siblingItemListKeys = Object.keys(siblingItemList) || [];

		if (
			(
				this.initiator === "itemGrid" &&
				(
					App.config.item.variationShowType === "combined" &&
					this.categoryItemSiblingsEnabled &&
					this.siblingItemListKeys.length > 0 &&
					this.getCategoryItemDataCount() === 0
				)
			) ||
			(
				this.initiator === "itemList" &&
				(
					this.categoryItemSiblingsEnabled &&
					this.siblingItemListKeys.length > 0 &&
					this.isFirst
				)
			)
		) {
			this.$store.dispatch("pushCategoryItemSiblings", this.siblingItemList);
			if (!this.siblingsCalled) {
				this.siblingsCalled = true;
				this.$store.dispatch("getCategoryItemSiblings", { overwriteUrl: this.overwriteUrl, imageOptions: this.imageOptions, urlAccessor: this.currentUrlAccessor });
			}
		}
		if (App.Eros.Config.general.benchmarkJS) console.timeEnd("App.eros.components.itemList.CategoryItem: mounted_" + this.item.variation.id); // eslint-disable-line no-console
	},

	jsonDataFields: ["itemDataRef"],

	computed: {
		item() {
			return this.itemData || this.itemDataRef;
		},

		/**
		 * returns itemData.item.storeSpecial
		 */
		storeSpecial() {
			return this.item.item.storeSpecial;
		},

		/**
		 * returns itemData.texts
		 */
		texts() {
			return this.item.texts;
		},

		itemPrice() {
			return this.$options.filters.specialOffer(
				this.item.prices.default.unitPrice.formatted,
				this.item.prices,
				"unitPrice",
				"formatted"
			);
		},

		itemSetPrice() {
			return this.$options.filters.currency(
				this.item.prices.default.price.value,
				this.item.prices.default.currency
			);
		},

		urlWithVariationId() {
			return !this.$ceres.config.item.showPleaseSelect || this.$ceres.initialPleaseSelect == 0 || this.forceUrlWithVariationId;
		},

		isSibling() {
			return (
				this.selectedSibling.hasOwnProperty("variationId") &&
				this.selectedSibling.variationId !== this.item.variation.id
			);
		},

		itemURL() {
			return this.isSibling ? this.selectedSibling.url : this.$options.filters.itemURL(this.item);
		},

		siblingImageURL() {
			if (this.secondSiblingSelected) {
				if (this.selectedSibling.images.second[this.currentUrlAccessor] === "") {
					return this.selectedSibling.images.first[this.currentUrlAccessor];
				}
				return this.selectedSibling.images.second[this.currentUrlAccessor];
			}
			return this.selectedSibling.images.first[this.currentUrlAccessor] === ""
				? ""
				: this.selectedSibling.images.first[this.currentUrlAccessor];
		},

		currentUrlAccessor() {
			return this.overwriteUrl.length ? "url" : this.imageUrlAccessor;
		},

		...mapState({
			showNetPrices: (state) => state.basket.showNetPrices,
			categoryItemSiblings: (state) => Object.keys(state.CategoryItemSiblings.categoryItemSiblings).length
		}),

		...mapGetters(["getCategoryItemDataCount"])
	},

	methods: {
		loadFirstImage() {
			const categoryImageCarousel = this.$refs.categoryImageCarousel;

			if (categoryImageCarousel) {
				categoryImageCarousel.loadFirstImage();
			}
		},
		changeVariation(variation) {
			variation.url =
				"/" +
				variation.urlPath +
				"_" +
				this.item.variation.itemId +
				"_" +
				variation.variationId +
				App.urls.trailingSlashSuffix;
			this.selectedSibling = variation;
		},
		showSecondImage() {
			if (App.Eros.Config.itemlists.enableSecondaryImageOnHover && document.body.classList.contains("no-touch")) {
				this.secondSiblingSelected = true;
			}
		},
		hideSecondImage() {
			this.secondSiblingSelected = false;
		},
		getSiblingUrl(url) {
			const includeLanguage = App.defaultLanguage != App.language;
			const urlPath = url || "";

			let link = "";

			if (includeLanguage) {
				link += "/" + App.language;
			}

			if (urlPath.charAt(0) !== "/") {
				link += "/";
			}

			if (urlPath && urlPath.length) {
				link += urlPath;
			}

			return link;
		}
	}
};
</script>
